import { CreditCard } from 'lucide-react';
import type { AccountLike } from '@tahi/core';
import { Chip } from '../../../ui';
import { copy } from '../copy';
import { LABEL } from '../styles';

type AccountRef = Pick<AccountLike, 'id' | 'name'>;

/** v0.7.6: a credit card offered under "From" (its method value is 'card:<payee id>'). */
export interface CardChoice {
  value: string;
  label: string;
}

export interface AccountChipsProps {
  /** "From", "Lands in", "To"… */
  label: string;
  accounts: readonly AccountRef[];
  value: string | null;
  onChange(id: string | null): void;
  /** Shown under the chips (e.g. which account is used when none is picked). */
  hint?: string;
  /** Tapping the chosen chip again clears it (default). Off for required picks like a transfer. */
  clearable?: boolean;
  /**
   * v0.7.6: credit cards after the accounts, for spending only. A movement uses an account or a
   * card, never both: picking one clears the other.
   */
  cards?: readonly CardChoice[];
  /** The picked card's method ('card:<id>'), or null. */
  card?: string | null;
  onCard?(method: string | null): void;
}

/** v0.5: which money account a movement uses, as chips like the limit (fund) chips. */
export function AccountChips({
  label,
  accounts,
  value,
  onChange,
  hint,
  clearable = true,
  cards = [],
  card = null,
  onCard,
}: AccountChipsProps) {
  const cardPicked = card !== null && cards.some((c) => c.value === card);
  return (
    <div className="flex flex-col gap-2">
      <span aria-hidden="true" className={LABEL}>
        {label}
      </span>
      <div role="group" aria-label={label} className="flex flex-wrap gap-2">
        {accounts.map((a) => {
          const selected = !cardPicked && a.id === value;
          return (
            <Chip
              key={a.id}
              selected={selected}
              aria-pressed={selected}
              onClick={() => {
                if (cardPicked) onCard?.(null);
                onChange(selected && clearable ? null : a.id);
              }}
            >
              {a.name}
            </Chip>
          );
        })}
        {onCard
          ? cards.map((c) => {
              const selected = c.value === card;
              return (
                <Chip
                  key={c.value}
                  selected={selected}
                  aria-pressed={selected}
                  aria-label={copy.cardChip(c.label)}
                  onClick={() => {
                    if (selected) {
                      onCard(null);
                      return;
                    }
                    onCard(c.value);
                    if (value !== null) onChange(null);
                  }}
                >
                  <CreditCard aria-hidden="true" className="size-4 shrink-0 text-muted" />
                  {c.label}
                </Chip>
              );
            })
          : null}
      </div>
      {hint ? <p className="text-sm text-muted">{hint}</p> : null}
    </div>
  );
}
